import { HelpOutline } from '@mui/icons-material';
import {
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmHandler } from '../../../common/ConfirmModal';
import { PriceNumberFormat } from '../../../component/form/FormPriceInput';
import { DissolveValueDetail } from './OverlayDissolveDetail';
import { useLeftSumEntries } from './OverlayDissolveDetailValue.utils';
import { dashboardGridColumn, dashboardGridContainer } from './OverlayDissolveValues';
import { getValueWithVatCoefficientApplied } from './OverlayDissolveValues.utils';
import { calculateSum, SumTable, SumTableEntryType } from './SumTable';

export function OverlayDissolveDetailValue({
  selectedDetail,
  setSelectedDetail,
  surcharge,
  pricesIncludingVAT,
  vatCoefficient,
  onSave,
  onlyPurchase,
  requestApplication,
  residualSubsidy,
}: {
  selectedDetail: DissolveValueDetail;
  setSelectedDetail: (detail: DissolveValueDetail) => void;
  surcharge: number;
  pricesIncludingVAT?: boolean;
  vatCoefficient: number;
  onSave?: () => Promise<void>;
  onlyPurchase: boolean;
  requestApplication: (purchasePrice: number) => Promise<void>;
  residualSubsidy: number;
}) {
  const { t } = useTranslation();

  const [isFollowUp, setIsFollowUp] = useState(!!selectedDetail.commisionNumber);

  const surchargeValue = selectedDetail.isSurchargeCancelled ? 0 : surcharge;
  const isPriceReasonable =
    (selectedDetail.purchasePrice ?? 0) >=
    getValueWithVatCoefficientApplied(selectedDetail.value, vatCoefficient) * 0.2;

  const leftSumTableEntries = useLeftSumEntries(
    isFollowUp,
    selectedDetail.value,
    selectedDetail.date,
    residualSubsidy,
    surchargeValue
  );

  const displayedPurchase = getValueWithVatCoefficientApplied(
    selectedDetail.purchasePrice,
    vatCoefficient
  );

  const onPurchaseChange = (value: number) => {
    setSelectedDetail({ ...selectedDetail, purchasePrice: value / vatCoefficient });
  };

  const purchasePriceLabel =
    t('dashboard.contractsOverlay.dissolveValues.purchasePrice') +
    ' ' +
    (pricesIncludingVAT ? t('price.gross') : t('price.net'));

  if (onlyPurchase) {
    return (
      <Grid container {...dashboardGridContainer} key={selectedDetail.type}>
        <Grid {...dashboardGridColumn} />
        <Grid {...dashboardGridColumn}>
          <Stack gap={2}>
            <Typography variant="h5">
              {t('dashboard.contractsOverlay.dissolveValues.purchasePriceSum')}
            </Typography>
            <TextField
              InputProps={{
                inputComponent: PriceNumberFormat,
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              value={displayedPurchase}
              label={purchasePriceLabel}
              onChange={(e) => onPurchaseChange(Number(e.target.value))}
              variant="standard"
            />
            <Stack flexDirection="row-reverse" gap={2}>
              <Button
                variant="contained"
                disabled={!isPriceReasonable}
                onClick={() => requestApplication(selectedDetail.purchasePrice!)}
              >
                {t('dashboard.contractsOverlay.dissolveValues.requestApplication')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                disabled={!isPriceReasonable}
                onClick={onSave}
              >
                {t('save')}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  const handleSurchageCancellation = () => {
    confirmHandler.confirm(
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchargeModalTitle'),
      () => {
        setSelectedDetail({ ...selectedDetail, isSurchargeCancelled: true });
      },
      () => {},
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchargeText'),
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchageConfirmButtonText'),
      t('dashboard.contractsOverlay.dissolveValues.cancelSurchageCancelButtonText')
    );
  };

  const rightSumTableEntries = [
    {
      label: purchasePriceLabel,
      value: selectedDetail.purchasePrice ?? 0,
      type: SumTableEntryType.Value,
      omit: !isPriceReasonable,
    },
    {
      label: t('dashboard.contractsOverlay.dissolveValues.surcharge'),
      value: surchargeValue,
      type: SumTableEntryType.Addition,
    },
  ];

  const rightSum = calculateSum(rightSumTableEntries);

  rightSumTableEntries.push({
    label: t('dashboard.contractsOverlay.dissolveValues.dealerPurchasePrice'),
    value: rightSum,
    type: SumTableEntryType.Sum,
    omit: !isPriceReasonable,
  });

  return (
    <Grid container {...dashboardGridContainer} key={selectedDetail.type}>
      <Grid {...dashboardGridColumn}>
        <Stack gap={2}>
          <Typography variant="h5">
            {t('dashboard.contractsOverlay.dissolveValues.dissolveValueSum')}
          </Typography>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <FormControlLabel
              onChange={() => setIsFollowUp((prev) => !prev)}
              control={<Switch checked={isFollowUp} />}
              label={t('dashboard.contractsOverlay.dissolveValues.followUp')}
            />
            <Tooltip
              title={t('dashboard.contractsOverlay.dissolveValues.followUpHint')}
              placement="right"
            >
              <HelpOutline sx={{ fill: 'grey' }} />
            </Tooltip>
          </Stack>
          <SumTable entries={leftSumTableEntries} vatCoefficient={vatCoefficient} />
          {isFollowUp && (
            <TextField
              value={selectedDetail.commisionNumber}
              label={t('dashboard.contractsOverlay.dissolveValues.commissionNumber')}
              variant="standard"
              onChange={(evt) =>
                setSelectedDetail({ ...selectedDetail, commisionNumber: evt.target.value })
              }
              required
            />
          )}
        </Stack>
      </Grid>
      <Grid {...dashboardGridColumn}>
        <Stack gap={2}>
          <Typography variant="h5">
            {t('dashboard.contractsOverlay.dissolveValues.purchasePriceSum')}
          </Typography>
          <TextField
            InputProps={{
              inputComponent: PriceNumberFormat,
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
            value={displayedPurchase}
            label={purchasePriceLabel}
            onChange={(e) => onPurchaseChange(Number(e.target.value))}
            variant="standard"
          />
          <SumTable highlight entries={rightSumTableEntries} vatCoefficient={vatCoefficient} />
          <Stack flexDirection="row-reverse" gap={2}>
            <Button
              variant="contained"
              disabled={!isPriceReasonable}
              onClick={async () => await requestApplication(selectedDetail.purchasePrice!)}
            >
              {t('dashboard.contractsOverlay.dissolveValues.requestApplication')}
            </Button>
            {selectedDetail.surchargeCancellationPossible && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSurchageCancellation}
                disabled={selectedDetail.isSurchargeCancelled}
              >
                {t('dashboard.contractsOverlay.dissolveValues.cancelSurcharge')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              disabled={!isPriceReasonable}
              onClick={onSave}
            >
              {t('save')}
            </Button>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}
